import { useTranslation } from "react-i18next";
import {
  Box,
  Chip,

  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import LinkIcon from "@mui/icons-material/Link";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

interface RecordingLinkCardProps {
  customerData: any;
  generatedUrl: string;
  createdAt: Date | null;
  isExpired: boolean;
  isLoading: boolean;
  expirationLabel: string;
  statusLabel: string;
  statusColor: "success" | "error" | "warning";
  isRefreshing: boolean;
  onCopyLink: () => void;
  onOpenRequestModal: () => void;
  onManualRefresh: () => void;
}

const RecordingLinkCard = ({
  customerData,
  generatedUrl,
  createdAt,
  isExpired,
  isLoading,
  expirationLabel,
  statusLabel,
  statusColor,
  isRefreshing,
  onCopyLink,
  onOpenRequestModal,
  onManualRefresh,
}: RecordingLinkCardProps) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();

  const MetadataChips = () => (
    <Stack
      direction="row"
      spacing={0.75}
      sx={{ flexWrap: "wrap", gap: 0.75 }}
    >
      <Chip
        icon={customerData?.type === "WIDGET" ? <WidgetsOutlinedIcon sx={{ fontSize: 16 }} /> : <VideocamOutlinedIcon sx={{ fontSize: 16 }} />}
        label={customerData?.type === "WIDGET" ? t("Advanced Feedback") : t("Screen Recording")}
        size="small"
        variant="outlined"
      />
      <Chip
        icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
        label={`${t("Expires")}: ${expirationLabel}`}
        size="small"
        variant="outlined"
      />
      {customerData?.type !== "WIDGET" && (
        <Chip
          icon={<MicNoneOutlinedIcon sx={{ fontSize: 16 }} />}
          label={customerData?.includeAudio ? t("Audio Enabled") : t("No Audio")}
          size="small"
          variant="outlined"
        />
      )}
      <Chip
        icon={customerData?.deliveryMethod === "email" ? <EmailOutlinedIcon sx={{ fontSize: 16 }} /> : <LinkIcon sx={{ fontSize: 16 }} />}
        label={customerData?.deliveryMethod === "email" ? t("Sent via Email") : t("Link Copied")}
        size="small"
        variant="outlined"
      />
      {customerData?.status && (
        <Chip
          label={`${t("Status")}: ${statusLabel}`}
          color={statusColor}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      )}
    </Stack>
  );

  const UrlRow = ({ mb }: { mb?: number }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1.5,
        borderRadius: 1.5,
        backgroundColor: theme.palette.action.hover,
        border: `1px solid ${theme.palette.divider}`,
        mb: mb ?? 0,
      }}
    >
      <LinkIcon sx={{ color: theme.palette.text.secondary, fontSize: 20, flexShrink: 0 }} />
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontFamily: "monospace",
          fontSize: 13,
        }}
      >
        {generatedUrl}
      </Typography>
      <Tooltip title={t("Copy link to clipboard")}>
        <IconButton onClick={onCopyLink} size="small" color="primary">
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const AwaitingBanner = () => (
    <Box
      sx={{
        mt: 2.5,
        p: 2,
        borderRadius: 2,
        backgroundColor: theme.palette.warning.main + "0C",
        border: `1px solid ${theme.palette.warning.main}30`,
      }}
    >
      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
        {customerData?.type === "WIDGET"
          ? t("Awaiting customer feedback submission")
          : t("Awaiting customer video upload")}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        {customerData?.type === "WIDGET"
          ? t("The customer hasn't submitted their feedback yet")
          : t("The customer hasn't recorded and uploaded their video yet")}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        onClick={onManualRefresh}
        sx={{ cursor: "pointer", display: "inline-flex" }}
      >
        <RefreshIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
        <Typography
          variant="caption"
          color="primary"
          sx={{ textDecoration: "underline", fontWeight: 600 }}
        >
          {isRefreshing ? t("Checking...") : t("Check now")}
        </Typography>
      </Stack>
    </Box>
  );

  const isEmpty = !generatedUrl && !customerData?.status;

  return (
    <Box
      sx={{
        borderRadius: isEmpty ? "14px" : 3,
        overflow: "hidden",
        backgroundColor: isEmpty
          ? "transparent"
          : theme.palette.background.paper,
        border: isEmpty
          ? `1.5px dashed ${theme.palette.divider}`
          : `1px solid ${theme.palette.divider}`,
        boxShadow: isEmpty ? "none" : `0 1px 3px ${theme.palette.action.focus}`,
        ...(isEmpty && {
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${theme.palette.primary.main}0A 0%, ${theme.palette.primary.main}06 100%)`
              : `linear-gradient(135deg, ${theme.palette.primary.main}06 0%, ${theme.palette.primary.main}04 100%)`,
        }),
      }}
    >
      {/* Section Header — hidden when empty */}
      {!isEmpty && <Box
        sx={{
          px: 3,
          py: 2,
          bgcolor: (t) =>
            t.palette.mode === "dark"
              ? "rgba(255,255,255,0.02)"
              : "#f9fafb",
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: (t) =>
                t.palette.mode === "dark"
                  ? `${t.palette.primary.main}20`
                  : `${t.palette.primary.main}14`,
            }}
          >
            {customerData?.type === "WIDGET" ? (
              <FeedbackOutlinedIcon sx={{ fontSize: 20, color: "primary.main" }} />
            ) : (
              <VideocamOutlinedIcon sx={{ fontSize: 20, color: "primary.main" }} />
            )}
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.3 }}>
              {customerData?.type === "WIDGET"
                ? t("Request Customer Feedback")
                : t("Request Screen Recording")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {customerData?.type === "WIDGET"
                ? t("Collect feedback via the embedded sniffer widget")
                : t("Share a link for the customer to record their screen")}
            </Typography>
          </Box>
        </Stack>
        {/* {customerData?.status && (
          <Chip
            label={statusLabel}
            color={statusColor}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        )} */}
      </Box>}

      {/* Section Body */}
      <Box sx={{ p: 3 }}>
        {customerData?.status === "completed" ? (
          <Box>
            {createdAt && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                {t("Generated on")}{" "}
                {createdAt.toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Typography>
            )}

            {generatedUrl && <UrlRow mb={1.5} />}

            <Box sx={{ mb: 2 }}>
              <MetadataChips />
            </Box>

            <LoadingButton
              variant="contained"
              startIcon={<RefreshIcon />}
              loading={isLoading}
              onClick={onOpenRequestModal}
              size="small"
            >
              {t("Generate New Link")}
            </LoadingButton>
          </Box>
        ) : generatedUrl ? (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              {createdAt && (
                <Typography variant="caption" color="text.secondary">
                  {t("Generated on")}{" "}
                  {createdAt.toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
              )}
            </Box>

            <UrlRow />

            {customerData?.expiresIn && createdAt && (
              <Typography
                variant="caption"
                color={isExpired ? "error" : "text.secondary"}
                sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <AccessTimeIcon sx={{ fontSize: 14 }} />
                {isExpired
                  ? t("This link has expired")
                  : `${t("Expires on")} ${new Date(createdAt.getTime() + customerData.expiresIn * 1000).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`}
              </Typography>
            )}

            <Box sx={{ mt: 2 }}>
              <MetadataChips />
            </Box>

            {/* Inline awaiting banner for pending state */}
            {customerData?.status === "pending" && !isExpired && <AwaitingBanner />}

            <LoadingButton
              variant="outlined"
              startIcon={<RefreshIcon />}
              loading={isLoading}
              onClick={onOpenRequestModal}
              size="small"
              sx={{ mt: 2 }}
            >
              {t("Regenerate Link")}
            </LoadingButton>
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", py: 3 }}>
            {/* Icon badge */}
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "14px",
                mx: "auto",
                mb: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: (t) =>
                  t.palette.mode === "dark"
                    ? `${t.palette.primary.main}20`
                    : `${t.palette.primary.main}14`,
              }}
            >
              <SendOutlinedIcon
                sx={{ fontSize: 28, color: "primary.main" }}
              />
            </Box>

            <Typography
              variant="body1"
              fontWeight={800}
              fontSize="14px"
              color="text.primary"
              sx={{ mb: 0.5 }}
            >
              {t("No feedback link generated yet")}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              fontSize="12px"
              sx={{ display: "block", mb: 1, maxWidth: 380, mx: "auto", lineHeight: 1.7 }}
            >
              {t("Generate a secure link and share it with your customer to collect screen recordings or feedback.")}
            </Typography>

            {/* Steps */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 3,
                mb: 2.5,
                mt: 2,
              }}
            >
              {[
                { step: "1", label: t("Choose Type"), icon: <VideocamOutlinedIcon sx={{ fontSize: 16 }} /> },
                { step: "2", label: t("Generate Link"), icon: <LinkIcon sx={{ fontSize: 16 }} /> },
                { step: "3", label: t("Share & Collect"), icon: <SendOutlinedIcon sx={{ fontSize: 16 }} /> },
              ].map((s) => (
                <Box
                  key={s.step}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? `${t.palette.primary.main}18`
                          : `${t.palette.primary.main}12`,
                      color: "primary.main",
                    }}
                  >
                    {s.icon}
                  </Box>
                  <Typography fontSize="10px" fontWeight={700} color="text.secondary">
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            <LoadingButton
              size="small"
              variant="contained"
              color="primary"
              disableElevation
              startIcon={<VideocamOutlinedIcon sx={{ fontSize: 15 }} />}
              loading={isLoading}
              onClick={onOpenRequestModal}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 700,
                borderRadius: "8px",
                px: 3,
                py: 0.8,
                boxShadow: "none",
              }}
            >
              {t("Generate Recording Link")}
            </LoadingButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RecordingLinkCard;
